import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { adminApiClient } from '../../adminApiClient';
import {
  AdminAuditLogResponse,
  AUDIT_EVENT_TYPES,
  AUDIT_OPERATION_TYPES,
  AuditEventType,
  AuditOperationType,
  formatAuditCreatedAt,
  formatAuditCreatedAtFull,
  formatAuditRowDisplay,
  formatTimeZoneOptionLabel,
  getBrowserTimeZone,
  listIanaTimeZones,
} from '../../models/AdminAuditLog';
import { Organization } from '../../models/Organization';

type FilterDraft = {
  eventType: '' | AuditEventType;
  operationType: '' | AuditOperationType;
  actorUserId: string;
  targetId: string;
  organizationId: string;
  createdFrom: string;
  createdTo: string;
};

const EMPTY_FILTERS: FilterDraft = {
  eventType: '',
  operationType: '',
  actorUserId: '',
  targetId: '',
  organizationId: '',
  createdFrom: '',
  createdTo: '',
};

const FILTER_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm";

function parseFilterDateTime(value: string): Date | null {
  if (!value.trim()) {
    return null;
  }
  try {
    const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value.trim())
      ? `${value.trim()}:00`
      : value.trim();
    const parsed = parseISO(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

function formatFilterDateTime(value: Date | null): string {
  if (!value || Number.isNaN(value.getTime())) {
    return '';
  }
  return format(value, FILTER_DATETIME_FORMAT);
}

const TIME_ZONE_OPTIONS = listIanaTimeZones();

/** CREATE_BOOKING → Create Booking */
function humanizeEventType(eventType: string): string {
  return eventType
    .split('_')
    .map((part) => {
      const lower = part.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function eventAccentColor(
  eventType: AuditEventType,
  palette: { success: { main: string }; error: { main: string }; warning: { main: string }; text: { disabled: string } }
): string {
  if (
    eventType.startsWith('CREATE_') ||
    eventType.startsWith('ENABLE_') ||
    eventType.startsWith('ADD_') ||
    eventType.startsWith('APPROVE_') ||
    eventType.startsWith('CONFIRM_')
  ) {
    return palette.success.main;
  }
  if (
    eventType.startsWith('DELETE_') ||
    eventType.startsWith('DISABLE_') ||
    eventType.startsWith('REMOVE_') ||
    eventType.startsWith('REJECT_') ||
    eventType.startsWith('CANCEL_')
  ) {
    return palette.error.main;
  }
  if (
    eventType.startsWith('UPDATE_') ||
    eventType.startsWith('PROPOSE_') ||
    eventType.startsWith('REFUND_')
  ) {
    return palette.warning.main;
  }
  return palette.text.disabled;
}

const CONTEXT_HIGHLIGHT_KEYS: Array<{ key: string; labelKey: string; fallback: string }> = [
  { key: 'organizationName', labelKey: 'auditLogs.organizationName', fallback: 'Organization' },
  { key: 'spaceName', labelKey: 'auditLogs.spaceName', fallback: 'Space' },
  { key: 'roomName', labelKey: 'auditLogs.roomName', fallback: 'Room' },
  { key: 'bookerName', labelKey: 'auditLogs.bookerName', fallback: 'Booker' },
  { key: 'tutorName', labelKey: 'auditLogs.tutorName', fallback: 'Tutor' },
  { key: 'startTime', labelKey: 'auditLogs.startTime', fallback: 'Start' },
  { key: 'endTime', labelKey: 'auditLogs.endTime', fallback: 'End' },
  { key: 'bookingKind', labelKey: 'auditLogs.bookingKind', fallback: 'Kind' },
  { key: 'name', labelKey: 'auditLogs.subjectName', fallback: 'Name' },
  { key: 'bandName', labelKey: 'auditLogs.bandName', fallback: 'Band' },
];

function CopyableId({
  label,
  value,
  onCopied,
}: {
  label: string;
  value: string | null;
  onCopied: () => void;
}) {
  if (!value) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          variant="body2"
          sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}
        >
          {value}
        </Typography>
        <Tooltip title="Copy">
          <IconButton
            size="small"
            aria-label={`Copy ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              void navigator.clipboard.writeText(value).then(onCopied);
            }}
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

const AuditLogsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [draftFilters, setDraftFilters] = useState<FilterDraft>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterDraft>(EMPTY_FILTERS);
  const [rows, setRows] = useState<AdminAuditLogResponse[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AdminAuditLogResponse | null>(null);
  const [copiedOpen, setCopiedOpen] = useState(false);
  const [timeZone, setTimeZone] = useState(getBrowserTimeZone);

  const notifyCopied = () => setCopiedOpen(true);
  const rowLabels = {
    unknownActor: t('auditLogs.unknownActor', 'Unknown'),
    globalScope: t('auditLogs.globalScope', 'Global'),
    missingOrganization: '—',
  };
  const selectedOrganization =
    organizations.find((org) => org.id === draftFilters.organizationId) ?? null;

  useEffect(() => {
    let cancelled = false;
    const loadOrganizations = async () => {
      try {
        const data = await adminApiClient.getOrganizations();
        if (cancelled) {
          return;
        }
        const sorted = (Array.isArray(data) ? data : [])
          .filter((org): org is Organization => Boolean(org?.id && org?.name))
          .sort((a, b) => a.name.localeCompare(b.name));
        setOrganizations(sorted);
      } catch (err) {
        console.error('Error fetching organizations for audit filters:', err);
        if (!cancelled) {
          setOrganizations([]);
        }
      }
    };
    void loadOrganizations();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApiClient.getAuditLogs({
        eventType: appliedFilters.eventType || undefined,
        operationType: appliedFilters.operationType || undefined,
        actorUserId: appliedFilters.actorUserId || undefined,
        targetId: appliedFilters.targetId || undefined,
        organizationId: appliedFilters.organizationId || undefined,
        createdFrom: appliedFilters.createdFrom || undefined,
        createdTo: appliedFilters.createdTo || undefined,
        page,
        size: pageSize,
      });
      setRows(Array.isArray(data.content) ? data.content : []);
      setTotalElements(data.page?.totalElements ?? 0);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(t('auditLogs.fetchError', 'Failed to fetch audit logs'));
      setRows([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, pageSize, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleApplyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(0);
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('auditLogs.title', 'Audit logs')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('auditLogs.subtitle', 'Append-only admin history. Open a row for IDs and context.')}
          </Typography>
        </Box>
        <Autocomplete
          size="small"
          options={TIME_ZONE_OPTIONS}
          value={timeZone}
          disableClearable
          onChange={(_event, next) => {
            if (next) setTimeZone(next);
          }}
          getOptionLabel={(option) => formatTimeZoneOptionLabel(option)}
          isOptionEqualToValue={(option, value) => option === value}
          renderInput={(params) => (
            <TextField {...params} label={t('auditLogs.timezone', 'Timezone')} />
          )}
          sx={{ width: { xs: '100%', sm: 320 }, flexShrink: 0, mt: { sm: 0.5 } }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <FormControl size="small" sx={{ flex: 1.4, minWidth: 180 }}>
              <InputLabel id="audit-event-type-label">{t('auditLogs.eventType', 'Event')}</InputLabel>
              <Select
                labelId="audit-event-type-label"
                label={t('auditLogs.eventType', 'Event')}
                value={draftFilters.eventType}
                onChange={(e) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    eventType: e.target.value as FilterDraft['eventType'],
                  }))
                }
              >
                <MenuItem value="">
                  <em>{t('auditLogs.any', 'Any')}</em>
                </MenuItem>
                {AUDIT_EVENT_TYPES.map((eventType) => (
                  <MenuItem key={eventType} value={eventType}>
                    {humanizeEventType(eventType)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: { xs: '100%', sm: 140 } }}>
              <InputLabel id="audit-operation-type-label">
                {t('auditLogs.operationType', 'Operation')}
              </InputLabel>
              <Select
                labelId="audit-operation-type-label"
                label={t('auditLogs.operationType', 'Operation')}
                value={draftFilters.operationType}
                onChange={(e) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    operationType: e.target.value as FilterDraft['operationType'],
                  }))
                }
              >
                <MenuItem value="">
                  <em>{t('auditLogs.any', 'Any')}</em>
                </MenuItem>
                {AUDIT_OPERATION_TYPES.map((operationType) => (
                  <MenuItem key={operationType} value={operationType}>
                    {operationType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DateTimePicker
              label={t('auditLogs.createdFrom', 'From')}
              ampm={false}
              value={parseFilterDateTime(draftFilters.createdFrom)}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  createdFrom: formatFilterDateTime(value),
                }))
              }
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: { xs: '100%', sm: 210 } },
                },
                field: { clearable: true },
              }}
            />
            <DateTimePicker
              label={t('auditLogs.createdTo', 'To')}
              ampm={false}
              value={parseFilterDateTime(draftFilters.createdTo)}
              minDateTime={parseFilterDateTime(draftFilters.createdFrom) ?? undefined}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  createdTo: formatFilterDateTime(value),
                }))
              }
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: { xs: '100%', sm: 210 } },
                },
                field: { clearable: true },
              }}
            />
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              size="small"
              sx={{ flex: 1, minWidth: 0 }}
              label={t('auditLogs.actorUserId', 'Actor user ID')}
              value={draftFilters.actorUserId}
              onChange={(e) =>
                setDraftFilters((prev) => ({ ...prev, actorUserId: e.target.value }))
              }
            />
            <TextField
              size="small"
              sx={{ flex: 1, minWidth: 0 }}
              label={t('auditLogs.targetId', 'Target ID')}
              value={draftFilters.targetId}
              onChange={(e) =>
                setDraftFilters((prev) => ({ ...prev, targetId: e.target.value }))
              }
            />
            <Autocomplete
              size="small"
              options={organizations}
              value={selectedOrganization}
              onChange={(_event, org) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  organizationId: org?.id ?? '',
                }))
              }
              getOptionLabel={(org) => org.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('auditLogs.organizationName', 'Organization')}
                />
              )}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button variant="contained" onClick={handleApplyFilters}>
                {t('auditLogs.applyFilters', 'Apply filters')}
              </Button>
              <Button variant="text" onClick={handleClearFilters}>
                {t('auditLogs.clearFilters', 'Clear')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </LocalizationProvider>

      <Card variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {t('auditLogs.listTitle', 'Events')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('auditLogs.total', '{{count}} total', { count: totalElements })}
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', minHeight: 200 }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (muiTheme) =>
                  muiTheme.palette.mode === 'dark'
                    ? 'rgba(0,0,0,0.35)'
                    : 'rgba(255,255,255,0.65)',
                zIndex: 1,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && rows.length === 0 ? (
            <Box sx={{ px: 2.5, py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {t('auditLogs.empty', 'No audit events match these filters')}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: { xs: 96, sm: 118 },
                        pl: 2.5,
                        color: 'text.secondary',
                        fontWeight: 600,
                      }}
                    >
                      {t('auditLogs.createdAt', 'When')}
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell
                          sx={{
                            width: 148,
                            color: 'text.secondary',
                            fontWeight: 600,
                          }}
                        >
                          {t('auditLogs.actor', 'Actor')}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: 168,
                            color: 'text.secondary',
                            fontWeight: 600,
                          }}
                        >
                          {t('auditLogs.organizationName', 'Organization')}
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {t('auditLogs.eventType', 'Event')}
                    </TableCell>
                    <TableCell sx={{ width: 40, px: 1 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((log) => {
                    const accent = eventAccentColor(log.eventType, theme.palette);
                    const display = formatAuditRowDisplay(log, rowLabels);

                    return (
                      <TableRow
                        key={log.id}
                        hover
                        onClick={() => setSelectedLog(log)}
                        sx={{
                          cursor: 'pointer',
                          verticalAlign: 'top',
                          borderLeft: `3px solid ${accent}`,
                        }}
                      >
                        <TableCell
                          sx={{
                            py: 1.75,
                            pl: 2.5,
                            color: 'text.secondary',
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatAuditCreatedAt(log.createdAt, timeZone)}
                        </TableCell>
                        {!isMobile && (
                          <>
                            <TableCell sx={{ py: 1.75 }}>
                              <Stack spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                                <Typography variant="body2" noWrap title={display.actor}>
                                  {display.actor}
                                </Typography>
                                {log.actorRole ? (
                                  <Chip
                                    label={log.actorRole}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.7rem',
                                      '& .MuiChip-label': { px: 0.75 },
                                    }}
                                  />
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 1.75 }}>
                              <Typography
                                variant="body2"
                                noWrap
                                title={display.organization}
                                color={
                                  display.organization === rowLabels.globalScope ||
                                  display.organization === rowLabels.missingOrganization
                                    ? 'text.secondary'
                                    : 'text.primary'
                                }
                              >
                                {display.organization}
                              </Typography>
                            </TableCell>
                          </>
                        )}
                        <TableCell sx={{ py: 1.75 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {humanizeEventType(log.eventType)}
                          </Typography>
                          {display.details ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.25 }}
                              noWrap={!isMobile}
                            >
                              {display.details}
                            </Typography>
                          ) : null}
                          {isMobile ? (
                            <Stack spacing={0.5} sx={{ mt: 0.75 }} alignItems="flex-start">
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="caption" color="text.secondary">
                                  {t('auditLogs.actor', 'Actor')}: {display.actor}
                                </Typography>
                                {log.actorRole ? (
                                  <Chip
                                    label={log.actorRole}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      '& .MuiChip-label': { px: 0.75 },
                                    }}
                                  />
                                ) : null}
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {t('auditLogs.organizationName', 'Organization')}:{' '}
                                {display.organization}
                              </Typography>
                            </Stack>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ py: 1.75, px: 1 }}>
                          <ChevronRightIcon fontSize="small" color="action" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Divider />
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(_e, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Card>

      <Dialog
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedLog
            ? humanizeEventType(selectedLog.eventType)
            : t('auditLogs.detailTitle', 'Audit event')}
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {selectedLog.operationType === 'READ' ? (
                  <Chip label="READ" size="small" variant="outlined" />
                ) : null}
                <Typography variant="body2" color="text.secondary">
                  {formatAuditCreatedAtFull(selectedLog.createdAt, timeZone)}
                </Typography>
              </Stack>

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('auditLogs.actor', 'Actor')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body1">
                    {selectedLog.actorName ?? t('auditLogs.unknownActor', 'Unknown')}
                  </Typography>
                  {selectedLog.actorRole ? (
                    <Chip label={selectedLog.actorRole} size="small" variant="outlined" />
                  ) : null}
                </Stack>
              </Box>

              {CONTEXT_HIGHLIGHT_KEYS.some(
                ({ key }) =>
                  typeof selectedLog.context?.[key] === 'string' &&
                  String(selectedLog.context[key]).trim()
              ) ? (
                <Stack spacing={1}>
                  {CONTEXT_HIGHLIGHT_KEYS.map(({ key, labelKey, fallback }) => {
                    const value = selectedLog.context?.[key];
                    if (typeof value !== 'string' || !value.trim()) {
                      return null;
                    }
                    return (
                      <Box key={key}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t(labelKey, fallback)}
                        </Typography>
                        <Typography variant="body2">{value}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              ) : null}

              <CopyableId
                label={t('auditLogs.actorUserId', 'Actor user ID')}
                value={selectedLog.actorUserId}
                onCopied={notifyCopied}
              />
              <CopyableId
                label={t('auditLogs.targetId', 'Target ID')}
                value={selectedLog.targetId}
                onCopied={notifyCopied}
              />
              <CopyableId
                label={t('auditLogs.organizationId', 'Organization ID')}
                value={selectedLog.organizationId}
                onCopied={notifyCopied}
              />

              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {t('auditLogs.context', 'Raw context')}
                </Typography>
                {Object.keys(selectedLog.context ?? {}).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('auditLogs.emptyContext', 'No context')}
                  </Typography>
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      overflow: 'auto',
                      fontSize: 12,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {JSON.stringify(selectedLog.context ?? {}, null, 2)}
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>{t('common.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copiedOpen}
        autoHideDuration={2000}
        onClose={() => setCopiedOpen(false)}
        message={t('auditLogs.copied', 'Copied')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AuditLogsPage;
